import { supabase } from '../lib/supabaseClient';
import type { Notification, CreateNotificationData } from '../types/notification';

/**
 * Service de gestion des notifications utilisateurs
 */

/**
 * Récupère toutes les notifications d'un utilisateur
 */
export async function getUserNotifications(userId: string): Promise<Notification[]> {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Erreur lors de la récupération des notifications:', error);
    }
    return [];
  }
}

/**
 * Compte les notifications non lues d'un utilisateur
 */
export async function getUnreadCount(userId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Erreur lors du comptage des notifications non lues:', error);
    }
    return 0;
  }
}

/**
 * Marque une notification comme lue
 */
export async function markAsRead(notificationId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) throw error;
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Erreur lors du marquage de la notification comme lue:', error);
    }
    return false;
  }
}

/**
 * Marque toutes les notifications d'un utilisateur comme lues
 */
export async function markAllAsRead(userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false);

    if (error) throw error;
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
    }
    return false;
  }
}

/**
 * Crée une nouvelle notification (côté admin/service)
 */
export async function createNotification(data: CreateNotificationData): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('notifications')
      .insert([data]);

    if (error) throw error;
    if (import.meta.env.DEV) {
      console.log('✅ Notification créée:', data.title);
    }
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('❌ Erreur lors de la création de la notification:', error);
    }
    return false;
  }
}

/**
 * Crée une notification pour un utilisateur spécifique (depuis l'admin)
 */
export async function createNotificationForUser(
  userId: string,
  title: string,
  message: string,
  type: 'admin_message' | 'system' | 'candidature_status' | 'ticket_reply' | 'societe_status' | 'nouveaute' | 'badge_unlocked' | 'rank_change' = 'admin_message',
  link?: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('notifications')
      .insert([{
        user_id: userId,
        type,
        title,
        message,
        link: link || null,
        read: false
      }]);

    if (error) throw error;
    if (import.meta.env.DEV) {
      console.log('✅ Notification admin créée pour l\'utilisateur:', userId);
    }
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('❌ Erreur lors de la création de la notification admin:', error);
    }
    return false;
  }
}

/**
 * Envoie une nouveauté à tous les commerçants validés
 */
export async function sendNewsletterToValidatedCommerce(
  title: string,
  message: string,
  link?: string
): Promise<{ success: number; failed: number; total: number }> {
  try {
    // Récupérer tous les commerçants validés
    const { data: profiles, error: fetchError } = await supabase
      .from('profiles')
      .select('user_id')
      .eq('role', 'COMMERCE')
      .eq('status', 'VALIDATED');

    if (fetchError) throw fetchError;

    if (!profiles || profiles.length === 0) {
      return { success: 0, failed: 0, total: 0 };
    }

    // Créer les notifications pour tous les commerçants
    const notifications = profiles.map(profile => ({
      user_id: profile.user_id,
      type: 'nouveaute' as const,
      title,
      message,
      link: link || null,
      read: false
    }));

    const { error: insertError } = await supabase
      .from('notifications')
      .insert(notifications);

    if (insertError) throw insertError;

    if (import.meta.env.DEV) {
      console.log(`✅ Nouveauté envoyée à ${profiles.length} commerçants validés`);
    }

    return { success: profiles.length, failed: 0, total: profiles.length };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('❌ Erreur lors de l\'envoi de la nouveauté:', error);
    }
    return { success: 0, failed: 1, total: 0 };
  }
}

/**
 * Supprime les anciennes notifications (plus de 30 jours)
 */
export async function cleanOldNotifications(userId: string): Promise<void> {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    await supabase
      .from('notifications')
      .delete()
      .eq('user_id', userId)
      .lt('created_at', thirtyDaysAgo.toISOString());
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Erreur lors du nettoyage des anciennes notifications:', error);
    }
  }
}